import React from "react";
import {Row} from "@tanstack/react-table";
import {AutoTableInterface} from "@/features/types/AutoTableInterface";
import {getAvailableActions, StatusCode} from "@/features/types/StatusChangeInterface";
import {getUserName} from "@/features/controllers/AutoTableController";
import {AlertPopupProps} from "@/shared/components/AlertPopup";

export interface StatusChangeValidationResult {
    canProceed: boolean;
    alertType?: "stock2" | "userMismatch";
    alertData?: {
        rowStatus?: string;
        rowUserName?: string;
    };
}

// Używamy bezpośrednio props z AlertPopup - nie duplikujemy interfejsu
// Pomijamy onClose, bo to jest callback który przekazujemy osobno
export type AlertPropsWithoutOnClose = Omit<AlertPopupProps, "onClose">;

export interface StatusChangeServiceOptions {
    row: Row<AutoTableInterface>;
    targetStatusCode: StatusCode;
    currentUserName: string | null;
    onUserNameFetch?: (userName: string) => void;
}

/**
 * Waliduje czy można wykonać akcję zmiany statusu
 * Sprawdza stock2, userName mismatch itp.
 */
export const validateStatusChange = async (
    options: StatusChangeServiceOptions
): Promise<StatusChangeValidationResult> => {
    const {row, targetStatusCode, currentUserName, onUserNameFetch} = options;

    // Sprawdź czy próbujemy zrobić rezerwację na elemencie z stock2 = TAK
    // Walidacja dotyczy tylko akcji rezerwacji (targetStatusCode === "5")
    // Działa zarówno dla statusu "" (NULL) -> "5" jak i dla statusu "5" -> "5" (jeśli taka akcja istnieje)
    if (targetStatusCode === "5") {
        const stock2 = row.original.f_vis_mark_7;
        // Backend powinien zwracać f_vis_mark_7 jako number (bit → number) dzięki convertValueToType()
        // Ale na wypadek gdyby wartość była stringiem, używamy Number() do konwersji
        const stock2Value = Number(stock2);
        if (stock2Value === 1) {
            return {
                canProceed: false,
                alertType: "stock2",
            };
        }
    }

    // Dla akcji "" (pusty string = usunięcie rezerwacji) nie sprawdzamy userMismatch,
    // bo uprawnienia są już sprawdzane w getActionsForStatus
    if (targetStatusCode === "") {
        return {
            canProceed: true,
        };
    }

    // Pobierz userName jeśli nie jest jeszcze dostępny
    let userName: string | null = currentUserName?.trim() || null;
    if (!userName) {
        try {
            const fetchedUserName = await getUserName();
            userName = fetchedUserName.trim();
            onUserNameFetch?.(userName);
        } catch (err) {
            console.error("Błąd podczas pobierania userName:", err);
        }
    }

    // Walidacja userName - sprawdź czy użytkownik z tabelki zgadza się z zalogowanym
    // Ta walidacja dotyczy tylko akcji zamówienia (targetStatusCode === "4")
    // Dla innych akcji nie jest potrzebna
    if (targetStatusCode === "4") {
        const rowUserName = row.original.f_sales_user_name?.trim();
        const rowStatus = row.original.f_supp_2_status_desc?.trim();

        if (rowUserName && userName && rowUserName !== userName) {
            return {
                canProceed: false,
                alertType: "userMismatch",
                alertData: {
                    rowStatus,
                    rowUserName,
                },
            };
        }
    }

    return {
        canProceed: true,
    };
};

/**
 * Pobiera dostępne akcje dla danego statusu z uwzględnieniem uprawnień użytkownika
 */
export const getActionsForStatus = (
    currentStatusCode: string | null,
    row?: Row<AutoTableInterface>,
    isManager?: boolean,
    currentUserName?: string | null
): StatusCode[] => {
    const allActions = getAvailableActions(currentStatusCode);
    
    // Jeśli status to "5" (Zarezerw.), filtrujemy akcję "" (pusty string = usunięcie rezerwacji) na podstawie uprawnień
    if (currentStatusCode === "5" && row) {
        const rowUserName = row.original.f_sales_user_name?.trim();
        const canCancelReservation = isManager || (currentUserName && rowUserName === currentUserName);
        
        // Jeśli użytkownik nie może anulować rezerwacji, usuń "" z listy
        if (!canCancelReservation) {
            return allActions.filter(action => action !== "");
        }
    }
    
    return allActions;
};

/**
 * Formatuje datę na format DD.MM.YYYY
 */
const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "Brak daty";
    try {
        const date = new Date(dateStr);
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        return `${day}.${month}.${year}`;
    } catch {
        return dateStr;
    }
};

/**
 * Generuje props dla AlertPopup na podstawie typu błędu
 */
export const getAlertProps = (alertType: "stock2" | "userMismatch", alertData?: {
    rowStatus?: string;
    rowUserName?: string;
}): AlertPropsWithoutOnClose => {
    switch (alertType) {
        case "stock2":
            return {
                title: "Rezerwacja niedozwolona",
                description: (
                    <>
                        Stop, znacznik <span className="font-semibold">stock2</span>, rezerwacja niedostępna.
                    </>
                ),
                type: "error",
            };
        case "userMismatch":
            return {
                title: "Operacja niedozwolona",
                description: (
                    <div className="flex flex-col items-start">
                        <div className="mb-2">
                             <span className="font-semibold">STATUS: {alertData?.rowStatus || "nieznany"}</span>
                        </div>
                        <div className="text-sm">
                            Pojazd nie może być ofertowany jako zamówienie: 01 - Aktywne.
                        </div>
                        <div className="text-sm mt-1">
                            Rezerwacji dokonał użytkownik: <span className="font-semibold">{alertData?.rowUserName || "nieznany"}</span>.
                        </div>
                    </div>
                ),
                type: "error",
            };
        default:
            return {
                title: "Błąd",
                description: "Wystąpił nieoczekiwany błąd.",
                type: "error",
            };
    }
};

/**
 * Generuje props dla AlertPopup potwierdzającego anulowanie rezerwacji
 */
export const getCancelReservationAlertProps = (data: {
    f_ord_num: string | null;
    f_sales_user_name: string | null;
    f_sales_dat: string | null;
}): Omit<AlertPropsWithoutOnClose, "onConfirm"> => {
    const order_num = data.f_ord_num || "-";
    const user_name = data.f_sales_user_name || "Nieznany użytkownik";
    const sales_dat = data.f_sales_dat || null;

    return {
        title: "Anulowanie rezerwacji",
        type: "delete-confirm",
        description: (
            <>
                <div className="mb-2">
                    Czy na pewno chcesz anulować rezerwację pojazdu o numerze{" "}
                    <span className="font-semibold">{order_num}</span>?
                </div>
                <div className="flex flex-col items-start text-sm mt-2">
                    <div>Przypisane do uzytkownika <span className="font-semibold">{user_name}</span></div>
                    <div>Z datą czynności <span className="font-semibold"> {formatDate(sales_dat)}</span></div>
                </div>
            </>
        ),
    };
};

